import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { CheckCircle2, FileText, Loader2, Sparkles, Undo2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX = 10 * 1024 * 1024;

// Upload-Feld für Ausweis-Vorder-/Rückseite mit optionaler serverseitiger Foto-Optimierung
// (Zuschnitt auf den Kartenrand, Begradigung, Beleuchtungsausgleich). Ergibt ein JPEG ohne Login.
export const AusweisUploadBox = ({ label, file, onChange, error, testId, optimize, optimizedName, texts, onTooLarge }) => {
  const [state, setState] = useState({ loading: false, original: null, cropped: null, previewUrl: '' });
  const inputRef = useRef(null);

  useEffect(() => () => { if (state.previewUrl) URL.revokeObjectURL(state.previewUrl); }, [state.previewUrl]);

  const setPreview = (f) => {
    if (state.previewUrl) URL.revokeObjectURL(state.previewUrl);
    return f && f.type.startsWith('image/') ? URL.createObjectURL(f) : '';
  };

  const handleFile = async (f) => {
    if (!f) return;
    if (f.size > MAX) { onTooLarge?.(); return; }
    const isImage = f.type.startsWith('image/') || /\.(heic|heif)$/i.test(f.name);
    if (!optimize || !isImage) {
      setState({ loading: false, original: null, cropped: null, previewUrl: setPreview(f) });
      onChange(f);
      return;
    }
    onChange(f);
    setState({ loading: true, original: f, cropped: null, previewUrl: '' });
    try {
      const fd = new FormData();
      fd.append('file', f);
      const r = await axios.post(`${API}/contact/optimize-image`, fd, { responseType: 'blob', timeout: 60000 });
      const optimized = new File([r.data], optimizedName, { type: 'image/jpeg' });
      const cropped = r.headers['x-scan-cropped'] === '1';
      setState({ loading: false, original: f, cropped, previewUrl: URL.createObjectURL(optimized) });
      onChange(optimized);
    } catch {
      setState({ loading: false, original: null, cropped: null, previewUrl: setPreview(f) });
    }
  };

  const useOriginal = () => {
    if (!state.original) return;
    onChange(state.original);
    setState({ loading: false, original: null, cropped: null, previewUrl: setPreview(state.original) });
  };

  return (
    <div>
      <label className="block text-[#1B3A5C] text-xs tracking-wider font-medium mb-2">{label}</label>
      <label
        className={`relative flex flex-col items-center justify-center cursor-pointer border-2 border-dashed rounded-sm px-4 py-5 min-h-[150px] transition-colors overflow-hidden ${
          error ? 'border-red-500 bg-red-50' : file ? 'border-emerald-400 bg-emerald-50' : 'border-gray-300 bg-white hover:border-[#b8893d] hover:bg-[#fbf7f0]'
        }`}
        data-testid={`${testId}-box`}
      >
        <input
          ref={inputRef}
          type="file"
          accept="image/jpeg,image/png,image/heic,image/webp,application/pdf"
          className="hidden"
          data-testid={testId}
          onChange={(e) => { handleFile(e.target.files?.[0]); e.target.value = ''; }}
        />
        {state.loading ? (
          <>
            <Loader2 className="w-7 h-7 text-[#b8893d] animate-spin mb-2" />
            <span className="text-sm text-[#1B3A5C] font-medium">{texts.optimizing}</span>
          </>
        ) : file ? (
          <>
            {state.previewUrl && (
              <img src={state.previewUrl} alt="" className="max-h-28 max-w-full object-contain rounded-sm border border-emerald-200 bg-white mb-2" data-testid={`${testId}-preview`} />
            )}
            <span className="flex items-center gap-1.5 text-emerald-700 text-sm font-medium text-center break-all">
              <CheckCircle2 className="w-4 h-4 flex-shrink-0" /> {file.name}
            </span>
            <span className="text-emerald-600 text-xs mt-1 text-center">
              {(file.size / 1024 / 1024).toFixed(2)} MB · {texts.changeHint}
            </span>
            {state.original && (
              <span className="mt-1.5 inline-flex items-center gap-1 text-[11px] text-[#b8893d]" data-testid={`${testId}-optimized-badge`}>
                <Sparkles className="w-3 h-3" /> {state.cropped ? texts.optimizedCropped : texts.optimizedNoCrop}
              </span>
            )}
          </>
        ) : (
          <>
            <FileText className={`w-7 h-7 mb-2 ${error ? 'text-red-500' : 'text-[#b8893d]'}`} />
            <span className={`text-sm font-medium ${error ? 'text-red-600' : 'text-[#1B3A5C]'}`}>{texts.cta}</span>
            <span className="text-xs text-[#34536b] mt-1">{texts.formats}</span>
          </>
        )}
      </label>
      {state.original && !state.loading && (
        <button type="button" onClick={useOriginal} className="mt-1.5 inline-flex items-center gap-1 text-[11px] text-[#34536b] hover:text-[#b8893d]" data-testid={`${testId}-use-original`}>
          <Undo2 className="w-3 h-3" /> {texts.useOriginal}
        </button>
      )}
    </div>
  );
};
